import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { ProductSchema } from "../pos"
import { DamperServiceActionName } from "./damper-service"
import { Discount, LegacyCompatibleDiscountSchema } from "./discount"
import {
  ProductChoiceSchema,
  ProductDecisionsSchema,
} from "./evaluated-service-bike-part"
import {
  RequiredManualServiceActionSchema,
  RequiredPartActionSchema,
  RequiredWashSchema,
  ServiceProposalDependenciesSchema,
  ServiceProposalFeeSchema,
} from "./fresh-service-proposal"
import { ProposalFunctionNoticeSchema } from "./fresh-service-proposal"
import { ActiveSecondsSchema, InteractionEffortSchema } from "./job-timing"
import { PartFunction } from "./part-function"
import { PartGrade } from "./part-grade"
import { Receipt, ReceiptProduct } from "./receipt"
import { ServiceFee } from "./service-fee"
import { ServicePackageItem } from "./service-package"
import { ServiceAction } from "./service-part-action"
import { ReceptionSummary } from "./service-proposal-context"
import { ApprovalStatus } from "./work-order"

export interface AddonOrderPackage {
  packageName: string
  snapshotPackageId: string
}
export interface AvailablePart {
  partId: string
  partName: string
  partImage: string
  availableActions: ServiceAction[]
  laborPrices: Record<ServiceAction, number>
  package: Record<ServiceAction, AddonOrderPackage | undefined>
}
export interface AvailableWash {
  originalWashId: string
  washName: string
  washDescription: string | null
  laborPrice: number
  package: AddonOrderPackage | null
}
export interface AvailablePackage {
  originalPackageId: string
  packageName: string
  laborPrice: number
  includedMaterialCost: number | null
  defaultDiscount: Discount | null
  items: ServicePackageItem[]
  /**
   * Wash the package covers, if any. The receipt editor offers it as a
   * sub-action of the package so the mechanic can add it without it becoming
   * a separate full-price line.
   */
  wash: { originalWashId: string; name: string; price: number } | null
}
export interface DataRequiredForAddonOrder {
  availableParts: AvailablePart[]
  availableWashes: AvailableWash[]
  availablePackages: AvailablePackage[]
  /**
   * Packages already on the work order, in the same shape as
   * `availablePackages`. These are deliberately excluded from
   * `availablePackages` (so they can't be re-added from the top-level
   * package search), but the receipt editor needs their template
   * `items[].includedActions` to let mechanics add actions to a package
   * that's already on the order.
   */
  existingPackages: AvailablePackage[]
  availableFees: ServiceFee[]
  damperServiceActionNames: DamperServiceActionName[]
  customerBudget: number
  workOrderCostBeforeAddons: number
}

export const AddonPartToAcceptSchema = z.object({
  partId: z.string(),
  partName: z.string(),
  partImage: z.string().nullable(),
  partAction: z.nativeEnum(ServiceAction),
  /**
   * Store-specific display name for the action (e.g. a damper service the store
   * has renamed). Resolved server-side via `getDamperServiceActionName`. When
   * undefined the UI falls back to the default action label (e.g.
   * "Grundservice").
   */
  actionLabel: z.string().optional(),
  partGrade: z.nativeEnum(PartGrade).nullable(),
  price: z.number(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  comment: z.string().nullable(),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
  /**
   * Snapshot id of an existing (already-approved) package on the work order
   * that this part-action belongs to, or null when the action stands on its
   * own. Lets the receipt nest the action under its package (e.g. "Byt
   * styrlager" inside a service package) instead of listing it as an
   * individual item.
   */
  existingPackageSnapshotId: z.string().nullable(),
  /**
   * When true, the customer cannot decline this part-action — it is
   * pre-selected and locked in the marketplace.
   */
  isMandatory: z.boolean(),
  /**
   * Dependencies the mechanic attached to this part-action ("Beror på") —
   * other items in the same addon order that must also be accepted. Resolved
   * server-side from the stored `required*` ids. Reused by the marketplace's
   * "Beroenden" card and violation check.
   */
  requiredWash: RequiredWashSchema.nullable(),
  requiredPartActions: z.array(RequiredPartActionSchema),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
  /**
   * Whether the component does its job, as the mechanic marked it. Defaulted
   * so answered addon orders stored before it existed still parse.
   */
  partFunction: z.nativeEnum(PartFunction).default(PartFunction.WORKS),
  /** What the customer is told about a component that doesn't work. */
  functionNotice: ProposalFunctionNoticeSchema.nullable().default(null),
})
export type AddonPartToAccept = z.infer<typeof AddonPartToAcceptSchema>

export const AddonWashAlternativeToAcceptSchema = z.object({
  originalWashId: z.string(),
  snapshotWashId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number(),
  // Defaulted so answered addon orders stored before wash discounts existed
  // still parse on read.
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  isRecommended: z.boolean(),
})
export type AddonWashAlternativeToAccept = z.infer<
  typeof AddonWashAlternativeToAcceptSchema
>

export const AddonWashAlternativesToAcceptSchema = z.object({
  id: z.string(),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
  alternatives: z.array(AddonWashAlternativeToAcceptSchema),
  /**
   * Dependencies attached to the wash ("Beror på") — other items in the same
   * addon order the wash relies on. A wash never requires another wash, so no
   * `requiredWash` here.
   */
  requiredPartActions: z.array(RequiredPartActionSchema),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
})
export type AddonWashAlternativesToAccept = z.infer<
  typeof AddonWashAlternativesToAcceptSchema
>

export const AddonManualServiceActionToAcceptSchema = z.object({
  id: z.string(),
  name: z.string(),
  comment: z.string().nullable(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
  /**
   * When true, the customer cannot decline this manual action — it is
   * pre-selected and locked in the marketplace.
   */
  isMandatory: z.boolean(),
  /**
   * Dependencies attached to this manual action ("Beror på") — other items in
   * the same addon order it relies on. Resolved server-side from the stored
   * `required*` ids.
   */
  requiredWash: RequiredWashSchema.nullable(),
  requiredPartActions: z.array(RequiredPartActionSchema),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
})
export type AddonManualServiceActionToAccept = z.infer<
  typeof AddonManualServiceActionToAcceptSchema
>

export const AddonPackageActionToAcceptSchema = z.object({
  partId: z.string(),
  partName: z.string(),
  partImage: z.string().nullable(),
  action: z.nativeEnum(ServiceAction),
  /**
   * The mechanic's grade for the part, so the customer screen can draw the same
   * remaining-lifespan bar inside a package that it draws on a standalone
   * action.
   */
  partGrade: z.nativeEnum(PartGrade).nullable(),
  /**
   * Store-specific display name for the action (e.g. a renamed damper service),
   * resolved server-side. Falls back to the default action label when
   * undefined.
   */
  actionLabel: z.string().optional(),
  comment: z.string().nullable(),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
})
export type AddonPackageActionToAccept = z.infer<
  typeof AddonPackageActionToAcceptSchema
>

export const AddonPackageToAcceptSchema = z.object({
  id: z.string(),
  packageName: z.string(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  includedMaterialCost: z.number().nullable(),
  actions: z.array(AddonPackageActionToAcceptSchema),
})
export type AddonPackageToAccept = z.infer<typeof AddonPackageToAcceptSchema>

export const AddonOrderToAcceptSchema = z.object({
  parts: z.array(AddonPartToAcceptSchema),
  washAlternatives: AddonWashAlternativesToAcceptSchema.nullable(),
  manualServiceActions: z.array(AddonManualServiceActionToAcceptSchema),
  packages: z.array(AddonPackageToAcceptSchema),
  /**
   * Fees the addon order brought with it. The customer cannot decline them —
   * they are shown locked so the addon's price is honest, rather than being
   * folded into the already-approved part of the receipt. Same shape as the
   * proper-inspection proposal's fees so both reuse `ServiceProposalFeeCard`.
   */
  fees: z.array(ServiceProposalFeeSchema),
  /**
   * Aggregate view of every item that carries a "Beror på" dependency, shaped
   * exactly like the proper-inspection proposal's `dependencies` block so the
   * marketplace can render the shared `ServiceProposalDependencies` card.
   */
  dependencies: ServiceProposalDependenciesSchema,
})
export type AddonOrderToAccept = z.infer<typeof AddonOrderToAcceptSchema>

export interface AddonOrderProposalWithReceipt {
  proposal: AddonOrderToAccept
  currentReceipt: Receipt
}

/**
 * Everything the customer-facing addon-order screen renders.
 *
 * `reception` is the drop-off summary the service proposal also shows. It is
 * null when a proposal was already sent for this job — the customer has seen it
 * there, and repeating it on every addon order would be noise — and null when
 * the job has no reception at all.
 */
export interface AddonOrderPageData extends AddonOrderProposalWithReceipt {
  reception: ReceptionSummary | null
}

export const AddonPartActionSchema = z.object({
  partId: z.string().uuid(),
  comment: z.string().optional(),
  action: z.nativeEnum(ServiceAction),
  grade: z.nativeEnum(PartGrade).nullable(),
  partFunction: z.nativeEnum(PartFunction).default(PartFunction.WORKS),
  laborPrice: z.number().min(0).optional(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  products: z.array(ProductSchema).default([]),
  productChoices: z.array(ProductChoiceSchema).default([]),
  isMandatory: z.boolean().default(false),
  // "Beror på" dependencies on other items in this order. Referenced by their
  // stable business keys: part-actions by partId, manual actions by their
  // client-supplied id, wash by originalWashId. Optional so older clients
  // keep validating.
  requiredWashId: z.string().uuid().nullable().optional(),
  requiredPartIds: z.array(z.string().uuid()).optional(),
  requiredManualServiceActionIds: z.array(z.string().uuid()).optional(),
})

export const AddonWashAlternativeSchema = z.object({
  originalWashId: z.string().uuid(),
  isRecommended: z.boolean(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
})

export const AddonWashAlternativesSchema = z.object({
  alternatives: z.array(AddonWashAlternativeSchema).min(1),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
  // Group-level "Beror på" dependencies. A wash never requires another wash.
  requiredPartIds: z.array(z.string().uuid()).optional(),
  requiredManualServiceActionIds: z.array(z.string().uuid()).optional(),
})
export type AddonWashAlternatives = z.infer<typeof AddonWashAlternativesSchema>

export const AddonManualServiceActionSchema = z.object({
  // Client-supplied stable id so "Beror på" references to this manual action
  // survive draft → submit → persisted → delivered. Falls back to a
  // server-minted id when omitted.
  id: z.string().uuid().optional(),
  title: z.string(),
  comment: z.string().optional(),
  laborPrice: z.number().min(0),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  products: z.array(ProductSchema).default([]),
  productChoices: z.array(ProductChoiceSchema).default([]),
  isMandatory: z.boolean().default(false),
  // The "Manuella åtgärder" item this action was picked from, if any.
  customBookingSectionItemId: z.string().uuid().nullable().optional(),
  requiredWashId: z.string().uuid().nullable().optional(),
  requiredPartIds: z.array(z.string().uuid()).optional(),
  requiredManualServiceActionIds: z.array(z.string().uuid()).optional(),
})
export const AddonPackageActionSchema = z.object({
  partId: z.string().uuid(),
  action: z.nativeEnum(ServiceAction),
  comment: z.string().optional(),
  products: z.array(ProductSchema).default([]),
  productChoices: z.array(ProductChoiceSchema).default([]),
})
export type AddonPackageAction = z.infer<typeof AddonPackageActionSchema>

export const AddonPackageSchema = z.object({
  originalPackageId: z.string().uuid(),
  laborPrice: z.number().min(0).optional(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  actions: z.array(AddonPackageActionSchema).default([]),
})
export const AddonFeeSchema = z.object({
  originalFeeId: z.string().uuid(),
})
export type AddonFee = z.infer<typeof AddonFeeSchema>

const AddonOrderDraftProductSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  ean: z.string().nullable(),
  articleNumber: z.string().nullable(),
  acquisitionCost: z.number().nullable(),
  title: z.string(),
  price: z.number(),
  quantity: z.number(),
  needsOrdering: z.boolean(),
  expectedDeliveryDate: z.string().nullable(),
  discount: LegacyCompatibleDiscountSchema.nullable(),
})

const AddonOrderDraftProductChoiceSchema = z.object({
  id: z.string(),
  comment: z.string(),
  recommendedProductId: z.string(),
  products: z.array(AddonOrderDraftProductSchema),
})

const AddonOrderDraftPackageActionSchema = z.object({
  id: z.string(),
  partId: z.string(),
  partName: z.string(),
  action: z.nativeEnum(ServiceAction),
  label: z.string(),
  comment: z.string(),
  products: z.array(AddonOrderDraftProductSchema),
  productChoices: z.array(AddonOrderDraftProductChoiceSchema),
})

/**
 * One row of the Tilläggsorder editor, exactly as the CRM holds it
 * (`ActionRow` in the CRM's service-action-rows). Mirrors that interface so a
 * saved draft restores the editor as it was, row ids and dependencies
 * included.
 */
const AddonOrderDraftRowSchema = z.object({
  id: z.string(),
  rowType: z.enum(["part", "wash", "manual", "package", "fee"]),
  partId: z.string().nullable(),
  partName: z.string().nullable(),
  action: z.nativeEnum(ServiceAction).nullable(),
  actionLabel: z.string().nullable().optional(),
  customItemId: z.string().nullable().optional(),
  originalWashId: z.string().nullable(),
  originalPackageId: z.string().nullable(),
  originalFeeId: z.string().nullable(),
  grade: z.nativeEnum(PartGrade).nullable(),
  // Optional so drafts saved before it existed still parse.
  partFunction: z.nativeEnum(PartFunction).optional(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  includedMaterialCost: z.number().nullable().optional(),
  label: z.string(),
  products: z.array(AddonOrderDraftProductSchema),
  productChoices: z.array(AddonOrderDraftProductChoiceSchema),
  comment: z.string(),
  existingAction: z.nativeEnum(ServiceAction).nullable(),
  partActionExistingPackage: z
    .object({ packageName: z.string(), snapshotPackageId: z.string() })
    .nullable(),
  packageActions: z.array(AddonOrderDraftPackageActionSchema),
  manualServiceActionId: z.string().optional(),
  snapshotPackageId: z.string().optional(),
  damperBrandId: z.string().nullable(),
  manualCommitted: z.boolean(),
  isMandatory: z.boolean(),
  dependsOnRowIds: z.array(z.string()).optional(),
})

/** The Tilläggsorder a mechanic saved with "Spara utkast", shared per job. */
export const AddonOrderDraftContentSchema = z.object({
  rows: z.array(AddonOrderDraftRowSchema),
  customerApprovedVerbally: z.boolean(),
  messageToCustomer: z.string(),
  washIsMandatory: z.boolean(),
  washComment: z.string().nullable(),
})
export type AddonOrderDraftContent = z.infer<
  typeof AddonOrderDraftContentSchema
>

/** Body of "Spara utkast": the draft plus the compose time since last save. */
export const SaveAddonOrderDraftSchema = AddonOrderDraftContentSchema.extend({
  activeSeconds: ActiveSecondsSchema.optional(),
  effort: InteractionEffortSchema.optional(),
})
export type SaveAddonOrderDraft = z.infer<typeof SaveAddonOrderDraftSchema>

export const AddonOrderProposalSchema = z
  .object({
    partActions: z.array(AddonPartActionSchema),
    washAlternatives: AddonWashAlternativesSchema.nullable(),
    manualServiceActions: z.array(AddonManualServiceActionSchema),
    packages: z.array(AddonPackageSchema).default([]),
    fees: z.array(AddonFeeSchema).default([]),
    messageToCustomer: z.string().optional(),
    customerApprovedVerbally: z.boolean().optional(),
    sendViaSms: z.boolean().optional(),
    sendViaEmail: z.boolean().optional(),
    // Whether the customer gets the order text in the SMS itself rather than a
    // teaser linking to the tilläggsorder. Absent means true, like sendViaSms
    // above: this flow has always sent the full text, and flipping it would
    // silently stop customers receiving the proposal they are asked to answer.
    // The API sends the full text regardless when it fits in one segment.
    sendFullSms: z.boolean().optional(),
    // The editor state the order was composed from. Kept while the order awaits
    // the customer so withdrawing it can restore it as the job's draft.
    draftContent: AddonOrderDraftContentSchema.optional(),
    // Active seconds the mechanic spent composing since the last draft save
    activeSeconds: ActiveSecondsSchema.optional(),
    effort: InteractionEffortSchema.optional(),
    orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
    laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
    productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  })
  .refine(
    (schema) => {
      if (!schema.messageToCustomer) return true
      return schema.sendViaSms !== false || schema.sendViaEmail === true
    },
    {
      message: "common.validation.chooseChannel" satisfies TranslationKey,
      path: [],
    },
  )
export type AddonOrderProposal = z.infer<typeof AddonOrderProposalSchema>

export interface AddonOrderDraft {
  content: AddonOrderDraftContent
  updatedAt: Date
  updatedByName: string
}

/** Wrapped so "no draft" is an explicit `null`, not an empty response body. */
export interface AddonOrderDraftResponse {
  draft: AddonOrderDraft | null
}

/**
 * The conditions under which an addon order needs the customer to make a
 * decision: the order is over budget, contains a product choice to pick
 * between, or offers multiple wash alternatives. Shared by the API gate and the
 * CRM form so the two can never drift apart (drift is what caused within-budget
 * addons with a product choice to be wrongly handled).
 *
 * This is only the trigger set. The `hasPendingAddonOrder` (always requires a
 * response) and `customerApprovedVerbally` (skips it) overrides are applied by
 * each caller, since the API and CRM treat those cases differently.
 */
export function addonOrderNeedsCustomerDecision(input: {
  isOverBudget: boolean
  hasProductChoices: boolean
  hasMultipleWashAlternatives: boolean
}): boolean {
  return (
    input.isOverBudget ||
    input.hasProductChoices ||
    input.hasMultipleWashAlternatives
  )
}

export const AcceptAddonOrderSchema = z.object({
  acceptedPartIds: z.array(z.string().uuid()),
  selectedWashOriginalId: z.string().uuid().nullable().optional(),
  acceptedManualServiceActionIds: z.array(z.string().uuid()),
  acceptedPackageIds: z.array(z.string().uuid()).default([]),
  productDecisions: ProductDecisionsSchema,
})
export type AcceptAddonOrder = z.infer<typeof AcceptAddonOrderSchema>

/**
 * One settled addon order: what the customer was shown and how it was settled.
 * `isAutoAccepted` orders were never shown for approval — they fitted inside
 * the budget and every item was accepted on the customer's behalf.
 */
export interface AnsweredAddonOrder {
  proposal: AddonOrderToAccept
  answer: AcceptAddonOrder
  isAutoAccepted: boolean
  createdAt: Date
}

/**
 * Every addon order on a job, merged into one proposal and one answer, for the
 * customer's view-only look back at them.
 */
export interface AnsweredAddonOrderPageData {
  proposal: AddonOrderToAccept
  answer: AcceptAddonOrder
  /** Whether any of the merged orders was accepted within the budget. */
  hasAutoAccepted: boolean
  reception: ReceptionSummary | null
}

export const AddonOrderSubmissionResultSchema = z.object({
  autoAccepted: z.boolean(),
  smsFailed: z.boolean(),
})
export type AddonOrderSubmissionResult = z.infer<
  typeof AddonOrderSubmissionResultSchema
>

// Addon Order History Types
export interface AddonOrderHistoryItem {
  type: "part" | "wash" | "manualServiceAction" | "package" | "fee"
  name: string
  approvalStatus: ApprovalStatus
  laborPrice: number
  totalProductCost: number
  /**
   * The individual products attached to this item, so the "already sent"
   * receipt can show the same per-product breakdown the proposed items show.
   * `totalProductCost` is the aggregate of these; an empty array means the
   * item has no products (e.g. wash items, or packages rendered as lump sums).
   */
  products: ReceiptProduct[]
  action?: ServiceAction
  /**
   * For `part` items: store-specific display name for the action (e.g. a
   * renamed damper service), resolved server-side. Lets the "already sent"
   * receipt show the same name the mechanic picked instead of falling back to
   * the default action label (e.g. "Grundservice").
   */
  actionLabel?: string
  /**
   * For `part` items: snapshot id of the existing package on the work order
   * this part-action belongs to, or null when it stands on its own. Lets the
   * receipt nest the sent action under its package instead of listing it as an
   * individual item — mirrors `AddonPartToAccept.existingPackageSnapshotId`.
   */
  existingPackageSnapshotId?: string | null
  /**
   * For `package` items: the package task's snapshot id, matched against a
   * part item's `existingPackageSnapshotId` to nest part-actions under it.
   */
  snapshotPackageId?: string
}

export interface AddonOrderSummary {
  id: string
  sentAt: Date
  answeredAt: Date | null
  revokedAt: Date | null
  status: "pending" | "answered" | "auto-accepted" | "revoked"
  items: AddonOrderHistoryItem[]
  acceptedCount: number
  deniedCount: number
  pendingCount: number
}

export interface AddonOrderHistory {
  orders: AddonOrderSummary[]
}
